import {
  DeleteOutlined,
  FileTextOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { Button, Input, Table, Tooltip, Typography } from 'antd'
import type { SorterResult } from 'antd/es/table/interface'
import {
  ONTOLOGY_FIELD_INPUTS,
  type OntologyFieldType,
  type OntologyValueType,
} from 'features/dynamicOntology'
import { LibraDocumentContext } from 'features/libra/document/LibraDocumentContext'
import {
  getFieldSource,
  unwrapFieldValue,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldCategory,
  type FieldValues,
  type ItemProperty,
  type OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import isNil from 'lodash/isNil'
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import { v4 } from 'uuid'
import { z } from 'zod'

import type { ArrayTableSection } from './arrayTableSectionTypes'

const TextAreaCell: FC<{
  value: string | undefined
  onChange: (newValue: string) => void
}> = ({ value, onChange }) => (
  <Input.TextArea
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    autoSize={{ minRows: 1, maxRows: 8 }}
  />
)

type RowRecord = Record<string, unknown> & { _id?: string }
type TableRow = RowRecord & { _rowIndex: number; _id: string }

const ROW_PROVENANCE_KEY = '_provenance'

const RowProvenanceSourceSchema = z
  .object({
    document_id: z.string().min(1).optional(),
    document_name: z.string().min(1),
    pages: z.array(z.number().int().positive()),
  })
  .transform(({ document_id, document_name, pages }) => ({
    documentId: document_id,
    documentName: document_name,
    pages,
  }))

const RowProvenanceSchema = z.object({
  sources: z.array(RowProvenanceSourceSchema),
})

type RowProvenanceSource = z.infer<typeof RowProvenanceSourceSchema>

function getRowProvenanceSources(row: RowRecord): RowProvenanceSource[] {
  const parsed = RowProvenanceSchema.safeParse(row[ROW_PROVENANCE_KEY])
  return parsed.success ? parsed.data.sources : []
}

function shortDocumentName(documentName: string): string {
  const baseName = documentName.split(/[\\/]/).pop() ?? documentName
  return baseName.length > 24 ? `${baseName.slice(0, 21)}...` : baseName
}

const RowProvenanceCell: FC<{ row: RowRecord }> = ({ row }) => {
  const documentContext = useContext(LibraDocumentContext)
  const sources = getRowProvenanceSources(row)

  if (sources.length === 0) return <span style={{ color: '#999' }}>—</span>

  return (
    <SourceList>
      {sources.map((source) => {
        const firstPage = source.pages[0]
        const pagesLabel =
          source.pages.length > 0
            ? `p. ${source.pages.join(', ')}`
            : 'Page unknown'
        const canNavigate = Boolean(source.documentId && documentContext)
        return (
          <Tooltip
            key={`${source.documentId ?? source.documentName}:${source.pages.join(',')}`}
            title={`${source.documentName} · ${pagesLabel}`}>
            <Button
              type='link'
              size='small'
              icon={<FileTextOutlined />}
              disabled={!canNavigate}
              aria-label={`Open source ${source.documentName}, ${pagesLabel}`}
              onClick={() => {
                if (!source.documentId || !documentContext) return
                documentContext.setSelectedDocumentId(
                  source.documentId,
                  firstPage
                )
              }}>
              {shortDocumentName(source.documentName)}
              <SourcePage>{pagesLabel}</SourcePage>
            </Button>
          </Tooltip>
        )
      })}
    </SourceList>
  )
}

interface ResolvedColumn {
  key: string
  property: ItemProperty
}

interface ArrayTableSectionViewProps {
  config: ArrayTableSection
  ontologyFields: Record<string, OntologyField>
  fieldValues: FieldValues
  setFieldValues?: Dispatch<SetStateAction<FieldValues>>
}

type SortDirection = 'ascending' | 'descending'

interface SortState {
  field: string
  order: SortDirection
}

const ANTD_ORDER_TO_DIRECTION: Record<'ascend' | 'descend', SortDirection> = {
  ascend: 'ascending',
  descend: 'descending',
}

const DIRECTION_TO_ANTD_ORDER: Record<SortDirection, 'ascend' | 'descend'> = {
  ascending: 'ascend',
  descending: 'descend',
}

type ColumnComparator = (a: unknown, b: unknown) => number

const compareAsString: ColumnComparator = (a, b) =>
  String(a).toLowerCase().localeCompare(String(b).toLowerCase())

const compareAsNumber: ColumnComparator = (a, b) => Number(a) - Number(b)

const COLUMN_COMPARATORS: Record<OntologyFieldType, ColumnComparator> = {
  date: (a, b) => new Date(String(a)).getTime() - new Date(String(b)).getTime(),
  number: compareAsNumber,
  boolean: compareAsNumber,
  text: compareAsString,
  enum: compareAsString,
  object: compareAsString,
  array: compareAsString,
}

function compareRowValues(
  a: RowRecord,
  b: RowRecord,
  field: string,
  order: SortDirection,
  columnType: OntologyFieldType
): number {
  const aValue = a[field]
  const bValue = b[field]

  if (isNil(aValue)) return 1
  if (isNil(bValue)) return -1

  const comparison = COLUMN_COMPARATORS[columnType](aValue, bValue)
  return order === 'ascending' ? comparison : -comparison
}

export const ArrayTableSectionView: FC<ArrayTableSectionViewProps> = ({
  config,
  ontologyFields,
  fieldValues,
  setFieldValues,
}) => {
  const isReadonly = !setFieldValues
  // The user's sort choice, made via a column header. Tri-state so we can tell
  // "hasn't touched sorting" apart from "explicitly cleared it":
  //   undefined -> untouched, fall back to `defaultSort` below
  //   null      -> user cycled the column back to unsorted (no sort)
  //   SortState -> user picked an explicit column + direction
  const [userSortState, setUserSortState] = useState<
    SortState | null | undefined
  >(undefined)

  const resolvedColumns = useMemo<ReadonlyArray<ResolvedColumn>>(() => {
    const parentField = ontologyFields[config.field]
    if (
      parentField?.category !== FieldCategory.INPUT ||
      !parentField.items?.properties
    ) {
      return []
    }

    return Object.entries(parentField.items.properties)
      .map(([key, property]) => ({ key, property }))
      .sort((a, b) => (a.property.sortOrder ?? 0) - (b.property.sortOrder ?? 0))
  }, [ontologyFields, config.field])

  const columnTypeMap = useMemo<ReadonlyMap<string, OntologyFieldType>>(
    () =>
      new Map(
        resolvedColumns.map((col) => [col.key, col.property.type ?? 'text'])
      ),
    [resolvedColumns]
  )

  const getColumnType = useCallback(
    (fieldKey: string): OntologyFieldType =>
      columnTypeMap.get(fieldKey) ?? 'text',
    [columnTypeMap]
  )

  // Sort applied before the user picks a column: an explicit `config.defaultSort`
  // if it targets a real column, otherwise the first date column descending
  // (newest first). `null` when neither applies.
  const defaultSort = useMemo<SortState | null>(() => {
    const configured = config.defaultSort
    if (
      configured &&
      resolvedColumns.some((col) => col.key === configured.field)
    ) {
      return { field: configured.field, order: configured.order }
    }

    const firstDateColumn = resolvedColumns.find(
      (col) => (col.property.type ?? 'text') === 'date'
    )
    return firstDateColumn
      ? { field: firstDateColumn.key, order: 'descending' }
      : null
  }, [config.defaultSort, resolvedColumns])

  // `undefined` = untouched (use default); `null` = user cleared it (no sort).
  const activeSort = userSortState === undefined ? defaultSort : userSortState

  const rows = useMemo<ReadonlyArray<RowRecord>>(() => {
    const raw = unwrapFieldValue<Array<RowRecord>>(fieldValues[config.field])
    return Array.isArray(raw) ? raw : []
  }, [fieldValues, config.field])

  const handleCellChange = useCallback(
    (
      rowIndex: number,
      colField: string,
      newValue: OntologyValueType | undefined
    ) => {
      setFieldValues?.((prev) => {
        const currentRows =
          unwrapFieldValue<Array<RowRecord>>(prev[config.field]) ?? []
        const updatedRows = [...currentRows]
        updatedRows[rowIndex] = {
          ...updatedRows[rowIndex],
          [colField]: newValue,
        }
        const source = getFieldSource(prev[config.field])
        return { ...prev, [config.field]: wrapFieldValue(updatedRows, source) }
      })
    },
    [config.field, setFieldValues]
  )

  const handleAddRow = useCallback(() => {
    setFieldValues?.((prev) => {
      const currentRows =
        unwrapFieldValue<Array<RowRecord>>(prev[config.field]) ?? []
      const emptyRow: RowRecord = {
        _id: v4(),
        ...Object.fromEntries(
          resolvedColumns.map((col) => [col.key, undefined])
        ),
      }
      const source = getFieldSource(prev[config.field])
      return {
        ...prev,
        [config.field]: wrapFieldValue([...currentRows, emptyRow], source),
      }
    })
  }, [config.field, resolvedColumns, setFieldValues])

  // Sorting is a display-only concern: it reorders the rendered rows without
  // rewriting the stored array, so cell edits/deletes (keyed on the original
  // row index) stay correct. Once the user interacts, their choice fully wins
  // (including cycling back to unsorted) so the default no longer applies.
  const handleTableChange = useCallback(
    (
      _pagination: unknown,
      _filters: unknown,
      sorter: SorterResult<TableRow> | SorterResult<TableRow>[]
    ) => {
      if (Array.isArray(sorter)) return

      if (!sorter.order || !sorter.field) {
        setUserSortState(null)
        return
      }

      setUserSortState({
        field: sorter.field as string,
        order: ANTD_ORDER_TO_DIRECTION[sorter.order],
      })
    },
    []
  )

  const handleDeleteRow = useCallback(
    (rowIndex: number) => {
      setFieldValues?.((prev) => {
        const currentRows =
          unwrapFieldValue<Array<RowRecord>>(prev[config.field]) ?? []
        const updatedRows = currentRows.filter((_, i) => i !== rowIndex)
        const source = getFieldSource(prev[config.field])
        return {
          ...prev,
          [config.field]: wrapFieldValue(updatedRows, source),
        }
      })
    },
    [config.field, setFieldValues]
  )

  const tableColumns = useMemo(() => {
    const dataCols = resolvedColumns.map((col) => {
      const fieldType: OntologyFieldType = col.property.type ?? 'text'

      return {
        title: col.property.name ?? col.key,
        dataIndex: col.key,
        key: col.key,
        sorter: true,
        sortOrder:
          activeSort?.field === col.key
            ? DIRECTION_TO_ANTD_ORDER[activeSort.order]
            : null,
        showSorterTooltip: { title: 'Click to sort this column' },
        render: (value: unknown, record: TableRow) => {
          if (isReadonly) return <ReadOnlyCell value={value} />

          const onChange = (newValue: OntologyValueType | undefined) =>
            handleCellChange(record._rowIndex, col.key, newValue)

          if (fieldType === 'text')
            return (
              <TextAreaCell
                value={value as string | undefined}
                onChange={onChange}
              />
            )

          const InputComponent = ONTOLOGY_FIELD_INPUTS[fieldType]
          return (
            <InputComponent
              value={value as OntologyValueType}
              onChange={onChange}
              fieldDef={col.property}
            />
          )
        },
      }
    })

    const provenanceCols = rows.some(
      (row) => getRowProvenanceSources(row).length > 0
    )
      ? [
          {
            title: 'Source',
            key: '_source',
            width: 150,
            render: (_: unknown, record: TableRow) => (
              <RowProvenanceCell row={record} />
            ),
          },
        ]
      : []

    if (isReadonly) return [...dataCols, ...provenanceCols]

    const actionsCol = {
      title: '',
      key: '_actions',
      width: 50,
      render: (_: unknown, record: TableRow) => (
        <Button
          icon={<DeleteOutlined />}
          type='text'
          danger
          size='small'
          onClick={() => handleDeleteRow(record._rowIndex)}
        />
      ),
    }

    return [...dataCols, ...provenanceCols, actionsCol]
  }, [
    resolvedColumns,
    handleCellChange,
    handleDeleteRow,
    isReadonly,
    activeSort,
    rows,
  ])

  const dataSource = useMemo<Array<TableRow>>(() => {
    const mapped = rows.map((row, i) => {
      const stableKey = row._id ?? `__row_${i}`
      return {
        ...row,
        _id: stableKey,
        _rowIndex: i,
        key: stableKey,
      }
    })

    if (!activeSort) return mapped

    const { field, order } = activeSort
    const columnType = getColumnType(field)
    return [...mapped].sort((a, b) =>
      compareRowValues(a, b, field, order, columnType)
    )
  }, [rows, activeSort, getColumnType])

  if (resolvedColumns.length === 0) {
    return null
  }

  return (
    <SectionContainer>
      {config.title && (
        <Typography.Title level={5} style={{ margin: 0 }}>
          {config.title}
        </Typography.Title>
      )}
      <StyledTable
        dataSource={dataSource}
        columns={tableColumns}
        pagination={false}
        size='small'
        bordered
        locale={{ emptyText: 'No entries yet' }}
        onChange={handleTableChange}
      />
      {!isReadonly && (
        <AddRowButton
          onClick={handleAddRow}
          type='dashed'
          icon={<PlusOutlined />}
          block>
          Add Row
        </AddRowButton>
      )}
    </SectionContainer>
  )
}

const ReadOnlyCell: FC<{ value: unknown }> = ({ value }) => {
  if (value === undefined || value === null) {
    return <span style={{ color: '#999' }}>—</span>
  }
  return <span>{String(value)}</span>
}

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const StyledTable = styled(Table)`
  .ant-table-cell {
    vertical-align: top;
  }
` as typeof Table

const AddRowButton = styled(Button)`
  margin-top: 0.25rem;
`

const SourceList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;

  .ant-btn {
    height: auto;
    max-width: 100%;
    padding: 0;
    align-items: flex-start;
    white-space: normal;
    text-align: left;
  }

  .ant-btn > span:not(.anticon) {
    min-width: 0;
    overflow-wrap: anywhere;
  }
`

const SourcePage = styled.span`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  color: currentColor;
  font-size: 0.625rem;
  opacity: 0.7;
`
